import { FC, useState, useMemo } from 'react';
import { Button } from 'react-daisyui';
import DatePickerInput from '@/components/functional/DatePickerInput/DatePickerInput.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import TextArea from '@/components/functional/TextArea/TextArea.tsx';

type BookingRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: BookingRequestData) => void;
  carerName: string;
  isLoading?: boolean;
  availabilityDates?: { startDate: string; endDate: string };
};

export type BookingRequestData = {
  start_date?: string;
  start_time?: string;
  end_time?: string;
  notes?: string;
};

const BookingRequestModal: FC<BookingRequestModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  carerName: _carerName,
  isLoading = false,
  availabilityDates
}) => {
  const [formData, setFormData] = useState<BookingRequestData>({
    start_date: '',
    start_time: '09:00',
    end_time: '10:00',
    notes: '',
  });

  const [errors, setErrors] = useState({
    start_date: '',
  });

  // Generate time options (00:00 - 23:30, every 30 minutes)
  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({
          value: timeValue,
          label: timeValue,
        });
      }
    }
    return options;
  }, []);

  // Filter end time options to only show times after start_time
  const endTimeOptions = useMemo(() => {
    if (!formData.start_time) return timeOptions;
    
    return timeOptions.filter(option => {
      return option.value > formData.start_time!;
    });
  }, [timeOptions, formData.start_time]);

  // Create function to disable dates outside availability range
  const isDateDisabled = useMemo(() => {
    if (!availabilityDates?.startDate || !availabilityDates?.endDate) {
      return undefined;
    }
    
    const startDate = new Date(availabilityDates.startDate);
    const endDate = new Date(availabilityDates.endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    return (date: Date) => {
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      return checkDate < startDate || checkDate > endDate;
    };
  }, [availabilityDates]);


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const newErrors = {
      start_date: '',
    };

    if (!formData.start_date) {
      newErrors.start_date = 'Date is required';
    }

    setErrors(newErrors);

    // If there are errors, don't submit
    if (newErrors.start_date) {
      return;
    }

    onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="w-full p-5 sm:p-8">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-bold pr-4">Select preferred days and times</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none flex-shrink-0"
              disabled={isLoading}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              <div>
                <DatePickerInput
                  value={formData.start_date || ''}
                  onChange={(value) => {
                    setFormData({ ...formData, start_date: value });
                    setErrors({ ...errors, start_date: '' });
                  }}
                  placeholder="dd/mm/yyyy"
                  disabled={isLoading}
                  displayFormat="DD/MM/YYYY"
                  disabledDates={isDateDisabled}
                />
                {errors.start_date && (
                  <p className="text-red-500 text-xs mt-1">{errors.start_date}</p>
                )}
              </div>

              <div className="mt-2">
                {/* Mobile: stacked */}
                <div className="flex flex-col gap-2 sm:hidden">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm w-6 shrink-0">From</span>
                    <SelectInput
                      value={formData.start_time || '09:00'}
                      onChange={(value) => {
                        const newFormData = { ...formData, start_time: value };
                        if (formData.end_time && formData.end_time <= value) {
                          const nextTimeSlot = timeOptions.find(opt => opt.value > value);
                          if (nextTimeSlot) newFormData.end_time = nextTimeSlot.value;
                        }
                        setFormData(newFormData);
                      }}
                      options={timeOptions}
                      disabled={isLoading}
                      wrapperClassName="flex-1"
                      placeholder="00:00"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-sm w-6 shrink-0">To</span>
                    <SelectInput
                      value={formData.end_time || '10:00'}
                      onChange={(value) => setFormData({ ...formData, end_time: value })}
                      options={endTimeOptions}
                      disabled={isLoading}
                      wrapperClassName="flex-1"
                      placeholder="00:00"
                    />
                  </div>
                </div>
                {/* Desktop: side by side */}
                <div className="hidden sm:flex gap-3 items-center">
                  <SelectInput
                    value={formData.start_time || '09:00'}
                    onChange={(value) => {
                      const newFormData = { ...formData, start_time: value };
                      if (formData.end_time && formData.end_time <= value) {
                        const nextTimeSlot = timeOptions.find(opt => opt.value > value);
                        if (nextTimeSlot) newFormData.end_time = nextTimeSlot.value;
                      }
                      setFormData(newFormData);
                    }}
                    options={timeOptions}
                    disabled={isLoading}
                    wrapperClassName="flex-1"
                    placeholder="00:00"
                  />
                  <span className="text-gray-500">to</span>
                  <SelectInput
                    value={formData.end_time || '10:00'}
                    onChange={(value) => setFormData({ ...formData, end_time: value })}
                    options={endTimeOptions}
                    disabled={isLoading}
                    wrapperClassName="flex-1"
                    placeholder="00:00"
                  />
                </div>
              </div>
            </div>

            <TextArea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add travel/expected costs (optional)"
              rows={4}
              disabled={isLoading}
            />

            <Button
              type="submit"
              className="w-full bg-black text-white border-none hover:opacity-90 rounded-lg py-3"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingRequestModal;
